import { ReactNode, useEffect, useState } from "react";
import {
  DotsThreeIcon,
  FlagPennantIcon,
  PencilSimpleIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { VenueReview } from "common/types";
import { bucketUrlPrefix } from "common/config";
import { useI18N } from "common/hooks";
import { getTimeAgo } from "common/utils/general";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";
import ImageListViewer from "@/components/ImageListViewer/ImageListViewer";
import ScrollContainer from "react-indiana-drag-scroll";
import { createPortal } from "react-dom";
import FullImageViewer from "@/components/FullImageViewer";

export interface ExperienceCardProps {
  review: VenueReview;
  hasMenu?: boolean;
  /** Report this experience — the only action available on someone else's. */
  onMenuClick?: () => void;
  /** DEV-544 — the signed-in user, so the card can tell whose experience this is. */
  currentUserId?: number | string;
  onEdit?: () => void;
  onDelete?: () => void;
}

const ExperienceCard = ({
  review,
  hasMenu = true,
  onMenuClick,
  currentUserId,
  onEdit,
  onDelete,
}: ExperienceCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const i18n = useI18N();

  // Compared by value: `review.userId` comes straight off the API while `user.id`
  // reaches us from auth, and a type mismatch would quietly hide the author's own
  // controls. Signed out (no id) never matches, which is the intent.
  const isOwnExperience =
    currentUserId != null &&
    review.userId != null &&
    Number(review.userId) === Number(currentUserId);

  type MenuAction = {
    key: string;
    label: string;
    icon: ReactNode;
    onSelect: () => void;
    className?: string;
  };

  // Edit needs a host that can open the composer; without one, offering it does nothing.
  const ownerActions: MenuAction[] = [
    ...(onEdit
      ? [
          {
            key: "edit",
            label: i18n.get("editExperience"),
            icon: <PencilSimpleIcon size={20} aria-hidden="true" />,
            onSelect: onEdit,
          },
        ]
      : []),
    ...(onDelete
      ? [
          {
            key: "delete",
            label: i18n.get("deleteExperience"),
            icon: (
              <TrashIcon
                size={20}
                className="text-fg-error-subtle"
                aria-hidden="true"
              />
            ),
            // Both variants: the dark one has to beat DropdownMenuItem's own
            // `dark:text-primary-150`, which otherwise repaints Delete lavender.
            className: "text-fg-error-subtle dark:text-fg-error-subtle",
            onSelect: onDelete,
          },
        ]
      : []),
  ];

  // The menu is the author's own actions and nothing else. Someone else's
  // experience offers nothing but Report, and a menu holding a single item is a
  // click in the way — that case renders a flag button instead, as the native
  // card does.
  const menuActions: MenuAction[] = isOwnExperience ? ownerActions : [];

  const [showImageList, setShowImageList] = useState<{
    show: boolean;
    current: number;
  }>({
    show: false,
    current: 0,
  });

  const handleOpenFullReviewImage = (photo) => {
    setCurrentReviewImage(photo);
    setFullReviewImageView(true);
  };

  const [fullReviewImageView, setFullReviewImageView] = useState(false);
  const [currentReviewImage, setCurrentReviewImage] = useState<any>(null);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  useEffect(() => setPortalRoot(document.body), []);

  // Computed live from createdAt — the backend `timeAgo` is a cached string
  // frozen at cache-build time and drifts stale.
  const timeAgoLabel = getTimeAgo(review.createdAt);

  return (
    <div className="flex flex-col items-start w-full">
      <article className="flex flex-col items-start w-full">
        {/* UserIcon InfoIcon Row */}
        <div className="flex gap-2 items-center px-4 py-3 w-full">
          {/* Avatar */}
          <div className="relative flex items-center shrink-0">
            {review.userProfilePhoto ? (
              <img
                className="w-9 h-9 rounded-full shadow-md object-cover"
                src={bucketUrlPrefix + review.userProfilePhoto}
                alt={review.userFirstName}
              />
            ) : (
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white font-medium select-none"
                style={{
                  backgroundColor: review?.userProfileColorCode ?? "#8751FF",
                  fontSize: "20px",
                }}
              >
                {review.userFirstName?.charAt(0)?.toUpperCase() ?? ""}
              </div>
            )}
          </div>

          {/* UserIcon Details */}
          <div className="flex flex-1 flex-col items-start leading-none min-w-0">
            <h3 className="label-m-medium text-fg-neutral-strong truncate w-full">
              {review.userFirstName} {review.userLastName}
            </h3>
            <p className="label-s-regular text-fg-neutral-subtle overflow-hidden text-ellipsis whitespace-nowrap w-full">
              {timeAgoLabel ? `${timeAgoLabel} | ` : ""}Shared a{" "}
              <span className="font-bold">
                &quot;
                {review.accessibility_experience === "cool" ? "Sweet" : "Sour"}
                &quot;
              </span>{" "}
              experience
            </p>
          </div>

          {/* Actions */}
          {hasMenu && (!isOwnExperience || menuActions.length > 0) && (
            <div className="flex gap-2 items-center justify-center shrink-0">
              {!isOwnExperience ? (
                <button
                  type="button"
                  onClick={() => onMenuClick?.()}
                  className="w-8 h-8 flex items-center justify-center rounded hover:bg-bg-brand-ghost-hover transition-colors focus-blue"
                  aria-label={i18n.get("reportExperience")}
                >
                  <FlagPennantIcon
                    size={20}
                    className="text-fg-error-subtle"
                    aria-hidden="true"
                  />
                </button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="w-8 h-8 flex items-center justify-center rounded hover:bg-bg-brand-ghost-hover transition-colors focus-blue"
                      aria-label={i18n.get("moreOptionsForThisExperience")}
                    >
                      <DotsThreeIcon
                        size={24}
                        className="text-fg-neutral-strong"
                        weight="bold"
                      />
                    </button>
                  </DropdownMenuTrigger>
                  {/* The shadcn defaults are the legacy palette (primary-50 /
                      secondary-800 navy), which reads as a different surface from
                      the card around it. Restate the v1 tokens the rest of this
                      card uses, each with its dark counterpart — Tailwind emits
                      variant utilities after plain ones, so an unqualified token
                      loses to the component's own `dark:` classes. */}
                  <DropdownMenuContent
                    align="end"
                    className="min-w-[200px] p-1 bg-surface-secondary dark:bg-surface-secondary border-border-token-brand-minimal dark:border-border-token-brand-minimal"
                  >
                    {menuActions.map((action) => (
                      <DropdownMenuItem
                        key={action.key}
                        onSelect={action.onSelect}
                        className={cn(
                          "min-h-11 gap-3 rounded-lg px-3 py-2 pl-3 label-m-medium",
                          "text-fg-neutral-strong dark:text-fg-neutral-strong",
                          "hover:bg-bg-brand-ghost-hover focus:bg-bg-brand-ghost-hover",
                          "dark:hover:bg-bg-brand-ghost-hover dark:focus:bg-bg-brand-ghost-hover",
                          action.className,
                        )}
                      >
                        {action.icon}
                        {action.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </div>

        {/* Tags and Content */}
        {((review.userAbility?.length ?? 0) > 0 ||
          review.notes ||
          (review.reviewPhotos?.length ?? 0) > 0) && (
          <div className="flex flex-col gap-4 w-full px-4 md:px-8 pb-6">
            {/* Tags */}
            {(review.userAbility?.length ?? 0) > 0 && (
              <div className="flex flex-wrap gap-2 items-center w-full">
                {review.userAbility.split(",").map((tag, index) => (
                  <div
                    key={index}
                    className="bg-bg-brand-secondary-default border border-border-token-brand-minimal flex items-center justify-center min-h-10 px-3 py-2 rounded-full hover:bg-bg-brand-secondary-hover active:bg-bg-brand-secondary-active transition-colors focus-blue"
                  >
                    <span className="label-m-medium text-fg-neutral-strong whitespace-nowrap">
                      {tag.trim()}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Review Text */}
            {review.notes && (
              <div className="flex flex-col items-start justify-center min-h-6 w-full">
                <p className="label-m-medium text-fg-neutral-strong">
                  {isExpanded ? review.notes : `${review.notes.slice(0, 150)}`}
                  {review.notes.length > 150 && (
                    <>
                      {!isExpanded && " ..."}
                      <button
                        type="button"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="ml-1 label-m-medium text-fg-brand-subtle hover:text-fg-brand-strong underline focus-blue"
                        aria-label={isExpanded ? "Show less" : "Show more"}
                      >
                        {isExpanded ? "less" : "more"}
                      </button>
                    </>
                  )}
                </p>
              </div>
            )}

            {/* Photos */}
            {(review.reviewPhotos?.length ?? 0) > 0 && (
              <div className="flex gap-2 items-start">
                <ScrollContainer className="flex flex-row space-x-2">
                  {review.reviewPhotos.map((photo, index) => (
                    <div
                      key={index}
                      className="relative rounded-lg shrink-0 w-16 h-16"
                    >
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          handleOpenFullReviewImage(photo);
                        }}
                        className="cursor-pointer flex-shrink-0 focus-blue rounded-lg"
                        aria-label={
                          photo.altText
                            ? `View photo: ${photo.altText}`
                            : `View review photo ${index + 1}`
                        }
                      >
                        <img
                          src={bucketUrlPrefix + photo.path}
                          className="absolute inset-0 w-full h-full object-cover rounded-lg"
                          alt={photo.altText || "Review photo"}
                        />
                      </button>
                    </div>
                  ))}
                </ScrollContainer>

                {/* Full image view */}
                {fullReviewImageView &&
                  currentReviewImage &&
                  portalRoot &&
                  createPortal(
                    <div className="fixed inset-0 z-[999] bg-black bg-opacity-90">
                      <FullImageViewer
                        images={review.reviewPhotos.map((photo, index) => ({
                          src: bucketUrlPrefix + photo.path,
                          alt: photo.altText || "Review photo" + (index + 1),
                          tag_name:
                            review.accessibility_experience === "cool"
                              ? "Sweet experience"
                              : "Sour experience",
                          category_name: photo.tagApplied,
                          created_at: null,
                          photo_type: "Review type",
                          review_photos_id: null,
                          width: photo.width,
                          height: photo.height,
                          sort_order: photo.sort_order,
                          qtags: [],
                          tags: [
                            {
                              tagName: "Review Photo",
                              categoryDisplaySectionId: "",
                            },
                          ],
                          displaySections: {},
                          venueId: review.businessId,
                          venueName: "Review venue name",
                          venueAddress: "Review venue address",
                          venueBrandId: "Review venue brand id",
                          venueBrandName: "Review venue brand name",
                          venueTypes: "Review venue types",
                          user_roles: { isGeneralUser: true },
                          isExperiencePhoto: true,
                          review_user_first_name: review.userFirstName,
                          review_user_last_name: review.userLastName,
                          review_user_profile_photo: review.userProfilePhoto
                            ? bucketUrlPrefix + review.userProfilePhoto
                            : undefined,
                          review_user_color: review.userProfileColorCode,
                          review_notes: review.notes,
                          review_time_ago: timeAgoLabel,
                          review_accessibility_experience:
                            review.accessibility_experience,
                        }))}
                        currentIndex={
                          review.reviewPhotos.findIndex(
                            (p) => p === currentReviewImage,
                          ) || 0
                        }
                        onClose={() => setFullReviewImageView(false)}
                        onFlag={() => {}}
                        showFlag={false}
                        showRightTab={true}
                      />
                    </div>,
                    portalRoot,
                  )}
              </div>
            )}

            <ImageListViewer
              title="Photos of Reviews"
              currentImage={showImageList.current}
              images={(review.reviewPhotos ?? []).map((photo) => ({
                id: photo.path,
                url: bucketUrlPrefix + photo.path,
                username: review.user?.name,
                timeAgo: timeAgoLabel,
                note: photo.altText,
              }))}
              visible={showImageList.show}
              onClose={() => {
                setShowImageList({
                  show: false,
                  current: 0,
                });
              }}
            />
          </div>
        )}
      </article>
    </div>
  );
};

export default ExperienceCard;
